/**
 * A function to create a log object for the 'wixPageIsBeingPreRendered' event (evid: 1)
 *
 * Event description: wix page is being pre-rendered.
 *
 * @param {wixPageIsBeingPreRenderedParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @return {LogObject<wixPageIsBeingPreRenderedParams>} LogObject<wixPageIsBeingPreRenderedParams>
 */
export function wixPageIsBeingPreRendered(params) {
    return { evid: 1, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'domainPostPurchaseFlowDomainSuccessPage' event (evid: 100)
 *
 * Event description: domain post-purchase flow -> domain success page.
 *
 * @param {domainPostPurchaseFlowDomainSuccessPageParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @return {LogObject<domainPostPurchaseFlowDomainSuccessPageParams>} LogObject<domainPostPurchaseFlowDomainSuccessPageParams>
 */
export function domainPostPurchaseFlowDomainSuccessPage(params) {
    return { evid: 100, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'firstAnonymous' event (evid: 1000)
 *
 * Event description: first anonymous.
 *
 * @param {firstAnonymousParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @property {Url} http_referrer - http_referrer
 * @property {string} utm_campaign - campaign_id
 * @property {string} ad_position - ad_position
 * @property {string} statusCode - response status code
 * @property {string} search_terms - search_terms
 * @property {string} ttclid - ttclid
 * @property {string} fbp_cookie - Facebook tracking cookie
 * @property {string} browser_language - browser_language
 * @property {string} fbc_cookie - Facebook tracking cookie
 * @property {string} googlead_domain - googlead_domain
 * @property {string} ad_group - ad_group
 * @property {string} ad_match_type - ad_match_type
 * @property {string} subsub - subsub
 * @property {string} segment - segment
 * @property {string} gclid - gclid
 * @property {string} user_agent - user_agent
 * @property {string} wbraid - wbraid
 * @property {string} gbraid - gbraid
 * @property {string} ad_domain - ad_domain
 * @property {string} subsegment - subsegment
 * @property {string} purpose_header - purpose_header
 * @property {string} profile - profile
 * @property {string} ad_keywords - ad_keywords
 * @property {string} experiment_id - experiment_id
 * @property {string} ad_id - ad_id
 * @property {string} referrer - referrer
 * @property {string} googlead_referral - googlead_referral
 * @property {Url} app_url - app_url
 * @property {string} se_address - se_address
 * @property {string} se_name - se_name
 * @property {string} referral - referrer
 * @property {string} fbclid - UTM param from http referrer
 * @property {string} epik - UTM param from http referrer
 * @property {string} irclickid - UTM param from http referrer
 * @property {string} impact_click_id - UTM param from http referrer
 * @return {LogObject<firstAnonymousParams>} LogObject<firstAnonymousParams>
 */
export function firstAnonymous(params) {
    return { evid: 1000, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'pageView' event (evid: 1001)
 *
 * Event description: page view.
 *
 * @param {pageViewParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @property {Url} http_referrer - may contain utm_campaign and experiment_id parameters
 * @property {string} utm_campaign - campaign_id_local
 * @property {string} statusCode - response status code
 * @property {string} search_terms - search_terms
 * @property {string} ttclid - ttclid
 * @property {string} profile_local - profile_local
 * @property {string} fbp_cookie - Facebook tracking cookie
 * @property {string} browser_language - browser_language
 * @property {string} fbc_cookie - Facebook tracking cookie
 * @property {string} subsub_local - subsub_local
 * @property {string} googlead_domain_local - googlead_domain_local
 * @property {string} ad_id_local - ad_id_local
 * @property {string} ad_keywords_local - ad_keywords_local
 * @property {string} googlead_referral_local - googlead_referral_local
 * @property {string} ad_position_local - ad_position_local
 * @property {string} gclid - gclid
 * @property {string} user_agent - user_agent
 * @property {string} wbraid - wbraid
 * @property {string} gbraid - gbraid
 * @property {string} purpose_header - purpose_header
 * @property {string} segment_local - segment_local
 * @property {string} experiment_id - experiment_id_local
 * @property {string} ad_domain_local - ad_domain_local
 * @property {string} ad_match_type_local - ad_match_type_local
 * @property {string} referrer - referrer_local
 * @property {Url} app_url - app_url
 * @property {string} se_address - se_address
 * @property {string} subsegment_local - subsegment_local
 * @property {string} ad_group_local - ad_group_local
 * @property {string} se_name - se_name
 * @property {string} referral - referrer_local
 * @property {string} fbclid - UTM param from http referrer
 * @property {string} epik - UTM param from http referrer
 * @property {string} irclickid - UTM param from http referrer
 * @property {string} impact_click_id - UTM param from http referrer
 * @return {LogObject<pageViewParams>} LogObject<pageViewParams>
 */
export function pageView(params) {
    return { evid: 1001, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'unsubscribeEmailPageView' event (evid: 1006)
 *
 * Event description: Unsubscribe from notifications email page view (contact)
 *
 * @param {unsubscribeEmailPageViewParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @property {string} uuid - uuid in case it's wix user
 * @property {string} topic_id - topic_id
 * @property {string} contact_id - contact_id in case it's wix member
 * @return {LogObject<unsubscribeEmailPageViewParams>} LogObject<unsubscribeEmailPageViewParams>
 */
export function unsubscribeEmailPageView(params) {
    return { evid: 1006, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'unsubscribeEmailPageClick' event (evid: 1007)
 *
 * Event description: Unsubscribe email page click (unsubscribe/personalize_preferences/unsubscribe_all)
 *
 * @param {unsubscribeEmailPageClickParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @property {string} uuid - uuid (in case it's wix user)
 * @property {string} topic_id - topic_id
 * @property {string} cta - unsubscribe/personalize_preferences/unsubscribe_all
 * @property {string} contact_id - contact_id in case it's wix member
 * @return {LogObject<unsubscribeEmailPageClickParams>} LogObject<unsubscribeEmailPageClickParams>
 */
export function unsubscribeEmailPageClick(params) {
    return { evid: 1007, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'unsubscribeEmailPersonalizePreferencesClick' event (evid: 1008)
 *
 * Event description: Unsubscribe email personalize_preferences click (toggles on off)
 *
 * @param {unsubscribeEmailPersonalizePreferencesClickParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @property {string} prev_value - (item_name:an_alert_by_wix, toggle:true}
 * @property {string} topic_id - topic_id
 * @property {string} new_value - (item_name:an_alert_by_wix, toggle:true}
 * @property {string} contact_id - contact_id
 * @return {LogObject<unsubscribeEmailPersonalizePreferencesClickParams>} LogObject<unsubscribeEmailPersonalizePreferencesClickParams>
 */
export function unsubscribeEmailPersonalizePreferencesClick(params) {
    return { evid: 1008, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'domainPostPurchaseFlowNoWebsitePage' event (evid: 101)
 *
 * Event description: domain post-purchase flow -> no website page.
 *
 * @param {domainPostPurchaseFlowNoWebsitePageParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @return {LogObject<domainPostPurchaseFlowNoWebsitePageParams>} LogObject<domainPostPurchaseFlowNoWebsitePageParams>
 */
export function domainPostPurchaseFlowNoWebsitePage(params) {
    return { evid: 101, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'domainPostPurchaseFlowGotWebsite' event (evid: 102)
 *
 * Event description: domain post-purchase flow -> got website.
 *
 * @param {domainPostPurchaseFlowGotWebsiteParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @return {LogObject<domainPostPurchaseFlowGotWebsiteParams>} LogObject<domainPostPurchaseFlowGotWebsiteParams>
 */
export function domainPostPurchaseFlowGotWebsite(params) {
    return { evid: 102, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'domainPostPurchaseFlowWorkingOnIt' event (evid: 103)
 *
 * Event description: domain post-purchase flow -> working on it.
 *
 * @param {domainPostPurchaseFlowWorkingOnItParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @return {LogObject<domainPostPurchaseFlowWorkingOnItParams>} LogObject<domainPostPurchaseFlowWorkingOnItParams>
 */
export function domainPostPurchaseFlowWorkingOnIt(params) {
    return { evid: 103, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'preRenderedWixPageIsWasShownToUser' event (evid: 2)
 *
 * Event description: pre-rendered wix page is was shown to user.
 *
 * @param {preRenderedWixPageIsWasShownToUserParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @return {LogObject<preRenderedWixPageIsWasShownToUserParams>} LogObject<preRenderedWixPageIsWasShownToUserParams>
 */
export function preRenderedWixPageIsWasShownToUser(params) {
    return { evid: 2, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'prerenderingClassificationStage1HomePageStartLoadingPreJs' event (evid: 4)
 *
 * Event description: Fired when hp html code runs, used for prerendering identification process (prefetching)
 *
 * @param {prerenderingClassificationStage1HomePageStartLoadingPreJsParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @return {LogObject<prerenderingClassificationStage1HomePageStartLoadingPreJsParams>} LogObject<prerenderingClassificationStage1HomePageStartLoadingPreJsParams>
 */
export function prerenderingClassificationStage1HomePageStartLoadingPreJs(params) {
    return { evid: 4, src: 19, endpoint: 'pblc', params: params };
}
/**
 * A function to create a log object for the 'prerenderingClassificationStage2HomePageInteraction' event (evid: 5)
 *
 * Event description: Fired upon any user interaction in the hp, used for stage 2 of prerendering identification process. fired only on desktop.  (prefetching)
 *
 * @param {prerenderingClassificationStage2HomePageInteractionParams} params - The parameters for this event
 * @property {DateTime} date_created - date_created
 * @property {DateTime} _artificial_ts - date_created
 * @return {LogObject<prerenderingClassificationStage2HomePageInteractionParams>} LogObject<prerenderingClassificationStage2HomePageInteractionParams>
 */
export function prerenderingClassificationStage2HomePageInteraction(params) {
    return { evid: 5, src: 19, endpoint: 'pblc', params: params };
}
//# sourceMappingURL=index.js.map